import React, { useState, useEffect } from 'react';
import { Calculator, LogOut, LineChart, Settings as SettingsIcon, Medal, Moon, Sun, BookOpen, Video } from 'lucide-react';
import { Language } from '../types';
import { supabase } from '../lib/supabase';
import Settings from './Settings';
import Badges from './Badges';
import VideosModal from './VideosModal';
import AppTutorial from './AppTutorial';

interface HeaderProps {
  language: Language;
  onLanguageChange: (lang: Language) => void;
  onProgressClick: () => void;
  showProgressButton: boolean;
  isDarkMode: boolean;
  onToggleDarkMode: () => void;
  userRole: string | null;
  userName: string;
}

const Header: React.FC<HeaderProps> = ({
  language,
  onLanguageChange,
  onProgressClick,
  showProgressButton,
  isDarkMode,
  onToggleDarkMode,
  userRole,
  userName
}) => {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isBadgesOpen, setIsBadgesOpen] = useState(false);
  const [isVideosOpen, setIsVideosOpen] = useState(false);
  const [isSettingsMenuOpen, setIsSettingsMenuOpen] = useState(false);
  const [isLearningMenuOpen, setIsLearningMenuOpen] = useState(false);
  const [badgeCount, setBadgeCount] = useState(0);
  const [showTutorial, setShowTutorial] = useState(false);

  useEffect(() => {
    loadBadgeCount();
  }, [userName]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.learning-menu') && !target.closest('.learning-button')) {
        setIsLearningMenuOpen(false);
      }
      if (!target.closest('.settings-menu') && !target.closest('.settings-button')) {
        setIsSettingsMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const loadBadgeCount = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { count } = await supabase
        .from('user_badges')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      setBadgeCount(count || 0);
    } catch (error) {
      console.error('Error loading badge count:', error);
      setBadgeCount(0);
    }
  };

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setBadgeCount(0);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const getWelcomeMessage = () => {
    if (!userName || !userRole) return '';
    const roleText = userRole === 'teacher'
      ? (language === 'en' ? 'Teacher' : 'Enseignant')
      : (language === 'en' ? 'Student' : 'Élève');
    return language === 'en'
      ? `Welcome ${userName}! (${roleText})`
      : `Bienvenue ${userName} ! (${roleText})`;
  };

  return (
    <>
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 py-3 px-4 shadow-sm">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Calculator className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
              <span className="font-bold text-lg text-indigo-700 dark:text-indigo-300">MathMaster</span>
            </div>
            {userName && (
              <span className="text-slate-600 dark:text-slate-300 hidden sm:block">
                {getWelcomeMessage()}
              </span>
            )}
          </div>

          <nav className="hidden sm:flex items-center gap-6">
            <button
              onClick={() => window.location.href = '/'}
              className="text-slate-600 dark:text-slate-300 hover:text-indigo-700 transition-colors"
            >
              {language === 'en' ? 'Home' : 'Accueil'}
            </button>

            <div className="relative learning-menu">
              <button
                onClick={() => setIsLearningMenuOpen(!isLearningMenuOpen)}
                className="learning-button flex items-center gap-2 text-slate-600 dark:text-slate-300 hover:text-indigo-700 transition-colors"
              >
                <BookOpen className="h-5 w-5" />
                <span>{language === 'en' ? 'Learning' : 'Apprentissage'}</span>
              </button>

              {isLearningMenuOpen && (
                <div className="absolute left-0 mt-2 w-48 bg-white dark:bg-slate-700 rounded-lg shadow-lg border border-slate-200 dark:border-slate-600 py-1 z-10">
                  <button
                    onClick={() => {
                      setShowTutorial(true);
                      setIsLearningMenuOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 flex items-center gap-2"
                  >
                    <BookOpen className="h-4 w-4" />
                    {language === 'en' ? 'Tutorial' : 'Tutoriel'}
                  </button>

                  <button
                    onClick={() => {
                      onProgressClick();
                      setIsLearningMenuOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 flex items-center gap-2"
                  >
                    <LineChart className="h-4 w-4" />
                    {language === 'en' ? 'Progress' : 'Progrès'}
                  </button>

                  <button
                    onClick={() => {
                      setIsBadgesOpen(true);
                      setIsLearningMenuOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 flex items-center gap-2"
                  >
                    <Medal className="h-4 w-4" />
                    <span>{language === 'en' ? 'Badges' : 'Badges'}</span>
                    {badgeCount > 0 && (
                      <span className="ml-auto bg-indigo-600 text-white text-xs px-2 py-0.5 rounded-full">
                        {badgeCount}
                      </span>
                    )}
                  </button>

                  <button
                    onClick={() => {
                      setIsVideosOpen(true);
                      setIsLearningMenuOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 flex items-center gap-2"
                  >
                    <Video className="h-4 w-4" />
                    {language === 'en' ? 'Videos' : 'Vidéos'}
                  </button>
                </div>
              )}
            </div>

            <div className="relative settings-menu">
              <button
                onClick={() => setIsSettingsMenuOpen(!isSettingsMenuOpen)}
                className="settings-button flex items-center gap-2 text-slate-600 dark:text-slate-300 hover:text-indigo-700 transition-colors"
              >
                <SettingsIcon className="h-5 w-5" />
                <span>{language === 'en' ? 'Settings' : 'Paramètres'}</span>
              </button>

              {isSettingsMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-700 rounded-lg shadow-lg border border-slate-200 dark:border-slate-600 py-1 z-10">
                  <button
                    onClick={() => {
                      setIsSettingsOpen(true);
                      setIsSettingsMenuOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600"
                  >
                    {language === 'en' ? 'Profile Settings' : 'Paramètres du profil'}
                  </button>
                  
                  <div className="px-4 py-2">
                    <div className="flex items-center justify-between text-sm text-slate-700 dark:text-slate-200">
                      <span>{language === 'en' ? 'Language' : 'Langue'}</span>
                      <select
                        value={language}
                        onChange={(e) => onLanguageChange(e.target.value as Language)}
                        className="ml-2 bg-slate-50 dark:bg-slate-600 rounded border border-slate-200 dark:border-slate-500"
                      >
                        <option value="en">English</option>
                        <option value="fr">Français</option>
                      </select>
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      onToggleDarkMode();
                      setIsSettingsMenuOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 flex items-center"
                  >
                    {isDarkMode ? (
                      <>
                        <Sun className="h-4 w-4 mr-2" />
                        {language === 'en' ? 'Light Mode' : 'Mode clair'}
                      </>
                    ) : (
                      <>
                        <Moon className="h-4 w-4 mr-2" />
                        {language === 'en' ? 'Dark Mode' : 'Mode sombre'}
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={handleSignOut}
              className="flex items-center gap-2 text-slate-600 dark:text-slate-300 hover:text-red-600 transition-colors"
            >
              <LogOut className="h-5 w-5" />
              <span>{language === 'en' ? 'Sign Out' : 'Déconnexion'}</span>
            </button>
          </nav>
        </div>
      </header>

      {isSettingsOpen && (
        <Settings
          language={language}
          onClose={() => setIsSettingsOpen(false)}
        />
      )}

      {isBadgesOpen && (
        <Badges
          language={language}
          isOpen={isBadgesOpen}
          onClose={() => setIsBadgesOpen(false)}
          onBadgesLoaded={(count) => setBadgeCount(count)}
        />
      )}

      {isVideosOpen && (
        <VideosModal
          language={language}
          onClose={() => setIsVideosOpen(false)}
        />
      )}
      {showTutorial && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <AppTutorial language={language} onClose={() => setShowTutorial(false)} />
        </div>
      )}    
    </>
  );
};

export default Header;